/**
 * The data-core feature (M8 plan MC-h5a; call MS-6): registers `dataCore.decode` on the craft hook
 * (`craft/hook.ts`), so `UiActions.craft` reads a generic hold core for a blueprint fragment
 * (`craft/blueprints.ts decodeCore`) at the docked Mutual market station. MC-h5b adds the Market core-row button;
 * this feature adds no station tab. A read raises a station notice with the new fragment count (PF-5).
 */
import { FEATURE_ORDER, type GameFeature } from '../../app/feature'
import { BLUEPRINTS, decodeCore } from '../../craft/blueprints'
import { registerCraftHandler, type CraftCommandMap, type CraftHandler } from '../../craft/hook'
import type { CmdOutcome } from '../../ui/contracts'
import { fragmentResultLine } from '../../ui/models/outfitting'

/** After the career runtime (the registry is global, so the order only places it in the list). */
const ORDER = FEATURE_ORDER.career + 1

/** The handler (exported for the tests; the feature registers it): only at the docked station. */
export const decodeHandler: CraftHandler<CraftCommandMap['dataCore.decode']> = (cmd, ctx): CmdOutcome => {
  const here = ctx.station
  if (here === null || here.id !== cmd.stationId) {
    return ctx.refuse({ code: 'serviceUnavailable', message: BLUEPRINTS.text.notDocked, detail: { station: cmd.stationId } })
  }
  const r = decodeCore(ctx.career(), here, cmd.coreId)
  if (!r.ok) return ctx.refuse(r.error)
  // PF-5: the station notice says what the core gave (the Read button goes with the core).
  ctx.commit(r.value, [...r.effects, { kind: 'resultLine', text: fragmentResultLine(r.value, BLUEPRINTS.cap) }])
  return ctx.done()
}

let unregister: (() => void) | null = null

const feature: GameFeature = {
  name: 'dataCores',
  order: ORDER,
  init(): void {
    unregister?.()
    unregister = registerCraftHandler('dataCore.decode', decodeHandler)
  },
  dispose(): void {
    unregister?.()
    unregister = null
  },
}

export default feature
